import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import { useEmergencyPending, useDecideEmergencyLeave, useEmergencyApprovals } from '../../hooks';
import { useToast } from '../../components/ui/toast';

type Decision = 'paid' | 'unpaid' | 'absent';
const DECISION_LABEL: Record<Decision, string> = {
  paid: 'ลาได้เงิน',
  unpaid: 'ลาไม่ได้เงิน',
  absent: 'ขาดงาน',
};

const LEAVE_TYPE_LABEL: Record<string, string> = {
  sick: 'ลาป่วย',
  personal: 'ลากิจ',
  vacation: 'ลาพักร้อน',
  unpaid: 'ลาไม่รับเงิน',
  maternity: 'ลาคลอด',
};
const PERIOD_LABEL: Record<string, string> = {
  morning: 'ครึ่งวันเช้า',
  afternoon: 'ครึ่งวันบ่าย',
  custom: 'ระบุเวลาเอง',
};

type UrgentLeave = {
  leave_id: string;
  emp_code: string;
  emp_name?: string;
  date: string;
  leave_type: string;
  period?: string;
  reason: string | null;
  current_level: number;
  is_emergency?: boolean;
};

function orgLine(department?: string | null, position?: string | null): string {
  return [department && `แผนก ${department}`, position && `ตำแหน่ง ${position}`].filter(Boolean).join(' · ');
}

/** Content-only version used by the Approvals hub (tabs). */
export function HREmergencyLeaveContent({ onGoToLeaves }: { onGoToLeaves?: () => void } = {}) {
  const { data: items, isLoading } = useEmergencyPending();
  // fb#484: ใบลาที่ติดป้าย "ลาฉุกเฉิน" (ยื่นกระชั้นชิด) อยู่ในคิวใบลาปกติ — เดิมแท็บนี้ไม่แสดงเลย
  // HR เห็นป้ายแล้วกดมาแท็บนี้เจอ "ไม่มีลาฉุกเฉินรอตัดสิน" → แสดงที่นี่ด้วย (อนุมัติที่แท็บใบลา)
  const { data: approvals, isLoading: approvalsLoading } = useEmergencyApprovals();
  const urgent = ((approvals?.leaves ?? []) as unknown as UrgentLeave[]).filter((l) => l.is_emergency);
  const decide = useDecideEmergencyLeave();
  const toast = useToast();
  const [openId, setOpenId] = useState<string | null>(null);
  const [decision, setDecision] = useState<Decision>('paid');
  const [reason, setReason] = useState('');
  const [deductQuota, setDeductQuota] = useState(true);

  function startDecide(leaveId: string) {
    setOpenId(leaveId);
    setDecision('paid');
    setReason('');
    setDeductQuota(true);
  }

  async function submit(leaveId: string) {
    if (!reason.trim()) { toast.error('กรุณาระบุเหตุผล'); return; }
    try {
      await decide.mutateAsync({ leave_id: leaveId, decision, reason: reason.trim(), deduct_quota: deductQuota });
      setOpenId(null);
    } catch (e) { toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'); }
  }

  function dueLabel(due: string | null): { text: string; overdue: boolean } {
    if (!due) return { text: '', overdue: false };
    const ms = new Date(due).getTime() - Date.now();
    const days = Math.ceil(ms / 86_400_000);
    return { text: days >= 0 ? `เหลือ ${days} วัน` : `เลยกำหนด ${-days} วัน`, overdue: days < 0 };
  }

  if (isLoading || approvalsLoading) return <Loading />;

  return (
    <>
      <p className="text-sm text-gray-600 mb-4">
        ลาฉุกเฉินมี 2 แบบ — <b>ใบลาที่ยื่นกระชั้นชิด</b> (ลาป่วยวันเดียวกัน / ลากิจล่วงหน้าไม่ถึง 3 วัน) อนุมัติตามขั้นปกติที่แท็บใบลา / OT
        และ <b>พนักงานไม่ได้สแกนเข้างานแล้วกดขอลาฉุกเฉิน</b> ซึ่ง HR ต้องตัดสินว่าเป็นลาได้เงิน / ลาไม่ได้เงิน / ขาดงาน ก่อนปิดงวด
      </p>

      <h3 className="font-semibold mb-2">ใบลาฉุกเฉินรออนุมัติ ({urgent.length})</h3>
      {urgent.length === 0 ? (
        <div className="card p-4 text-sm text-gray-500 mb-5">ไม่มีใบลาฉุกเฉินรออนุมัติ</div>
      ) : (
        <div className="space-y-2 mb-5">
          {urgent.map((l) => (
            <div key={l.leave_id} className="card p-4">
              <div className="flex justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold">{l.emp_name ?? l.emp_code}</p>
                  <p className="text-xs text-gray-500">{l.emp_code} · {l.leave_id}</p>
                  <p className="text-sm text-gray-700 mt-1">
                    {LEAVE_TYPE_LABEL[l.leave_type] ?? l.leave_type}
                    {l.period && PERIOD_LABEL[l.period] && ` · ${PERIOD_LABEL[l.period]}`}
                    {' · '}{l.date}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">เหตุผลพนักงาน: {l.reason || '— ไม่ระบุ —'}</p>
                </div>
                <span className="badge-info shrink-0 self-start">รอขั้น L{l.current_level}</span>
              </div>
            </div>
          ))}
          {onGoToLeaves && (
            <button onClick={onGoToLeaves} className="btn-primary w-full text-sm">ไปอนุมัติที่แท็บใบลา / OT</button>
          )}
        </div>
      )}

      <h3 className="font-semibold mb-2">ไม่ได้สแกนแล้วขอลาฉุกเฉิน รอ HR ตัดสิน ({items?.length ?? 0})</h3>
      {!items || items.length === 0 ? (
        <div className="card p-4 text-sm text-gray-500">ไม่มีเคสรอตัดสิน</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const due = dueLabel(it.conditional_evidence_due);
            const org = orgLine(it.department, it.position);
            return (
              <div key={it.leave_id} className="card p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold">{it.emp_name ?? it.emp_code}</div>
                    <div className="text-xs text-gray-500">{it.emp_code} · {it.leave_id}{org && ` · ${org}`}</div>
                    <div className="text-sm text-gray-700 mt-1">ไม่ได้สแกนเข้างานวันที่ {it.date} แล้วกดขอลาฉุกเฉิน</div>
                    <div className="text-sm text-gray-600 mt-1">เหตุผลพนักงาน: {it.reason || '— ไม่ระบุ —'}</div>
                  </div>
                  {due.text && (
                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${due.overdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                      {due.text}
                    </span>
                  )}
                </div>

                {openId === it.leave_id ? (
                  <div className="mt-3 space-y-2 border-t pt-3">
                    <div className="flex gap-2">
                      {(Object.keys(DECISION_LABEL) as Decision[]).map((d) => (
                        <button
                          key={d}
                          onClick={() => setDecision(d)}
                          className={`flex-1 text-sm py-2 rounded-lg border ${decision === d ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-200'}`}
                        >
                          {DECISION_LABEL[d]}
                        </button>
                      ))}
                    </div>
                    <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="input" placeholder="เหตุผลการตัดสิน (บังคับ)" />
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={deductQuota} onChange={(e) => setDeductQuota(e.target.checked)} />
                      ตัดโควต้าวันลาด้วย
                    </label>
                    <div className="flex gap-2">
                      <button onClick={() => setOpenId(null)} className="btn-secondary flex-1 text-sm">ยกเลิก</button>
                      <button onClick={() => submit(it.leave_id)} disabled={decide.isPending} className="btn-primary flex-1 text-sm">ยืนยันการตัดสิน</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => startDecide(it.leave_id)} className="btn-primary w-full mt-3 text-sm">ตัดสินเคสนี้</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/** Standalone page (kept for direct linking from legacy URLs). */
export function HREmergencyLeave() {
  return (
    <AdminLayout title="ตรวจลาฉุกเฉิน">
      <HREmergencyLeaveContent />
    </AdminLayout>
  );
}
